import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback, useRef, useState, useEffect, useMemo, type FC } from 'react';
import { useWatch, type Control, type UseFormSetValue } from 'react-hook-form';

import { type ChecklistDecisionType, ChecklistDecisionTypeLabels } from 'types';

import { DECISION_TYPES, DECISION_TYPE_STYLES, type DialogFormValues } from './checklistItemInputOptions';
import { isFlagCreatingDecisionType } from './flagMappingOptions';
import { InlineEditableLabelInput } from './InlineEditableLabelInput';
import { OptionFlagMappingEditor } from './OptionFlagMappingEditor';

export interface InputOptionRowProps {
  inputIndex: number;
  optionIndex: number;
  control: Control<DialogFormValues>;
  setValue: UseFormSetValue<DialogFormValues>;
  removeOption: (index: number) => void;
}

interface DecisionTypeOptionProps {
  type: ChecklistDecisionType;
  onSelect: (type: ChecklistDecisionType) => void;
}

const DecisionTypeOption: FC<DecisionTypeOptionProps> = ({ type, onSelect }) => {
  const handleClick = useCallback(() => onSelect(type), [onSelect, type]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-center px-12 py-6 text-left hover:bg-canvas-subtle"
    >
      <span
        className={`inline-flex items-center rounded-full px-12 py-4 text-sm leading-none font-[570] whitespace-nowrap ${DECISION_TYPE_STYLES[type]}`}
      >
        {ChecklistDecisionTypeLabels[type]}
      </span>
    </button>
  );
};

export const InputOptionRow: FC<InputOptionRowProps> = ({
  inputIndex,
  optionIndex,
  control,
  setValue,
  removeOption,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const watchedLabel = useWatch({
    control,
    name: `inputs.${inputIndex}.options.${optionIndex}.label`,
  });
  const watchedDecisionType = useWatch({
    control,
    name: `inputs.${inputIndex}.options.${optionIndex}.decisionType`,
  });

  const handleRemove = useCallback(() => removeOption(optionIndex), [removeOption, optionIndex]);

  const handleLabelCommit = useCallback(
    (val: string) => {
      setValue(`inputs.${inputIndex}.options.${optionIndex}.label`, val);
      setValue(`inputs.${inputIndex}.options.${optionIndex}.value`, val);
    },
    [setValue, inputIndex, optionIndex],
  );

  const handleSelectDecisionType = useCallback(
    (type: ChecklistDecisionType) => {
      setValue(`inputs.${inputIndex}.options.${optionIndex}.decisionType`, type);
      if (!isFlagCreatingDecisionType(type)) {
        setValue(`inputs.${inputIndex}.options.${optionIndex}.flagMappings`, []);
        setValue(`inputs.${inputIndex}.options.${optionIndex}.requirementFlagSettingId`, undefined);
      }
      setDropdownOpen(false);
      setSearchValue('');
    },
    [setValue, inputIndex, optionIndex],
  );

  const handleToggleDropdown = useCallback(() => setDropdownOpen((o) => !o), []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => setSearchValue(''), []);

  const handleClearSearchMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  }, []);

  const filteredTypes = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    const sorted = DECISION_TYPES.toSorted((a, b) =>
      ChecklistDecisionTypeLabels[a].localeCompare(ChecklistDecisionTypeLabels[b]),
    );
    return q ? sorted.filter((t) => ChecklistDecisionTypeLabels[t].toLowerCase().includes(q)) : sorted;
  }, [searchValue]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    searchInputRef.current?.focus();
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchValue('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const selectedStyle = watchedDecisionType
    ? DECISION_TYPE_STYLES[watchedDecisionType]
    : 'bg-tag-container-default-volt text-tag-text-default-volt';
  const selectedLabel = watchedDecisionType ? ChecklistDecisionTypeLabels[watchedDecisionType] : 'Flag Type';

  return (
    <div>
      <div className="mb-4 flex items-center gap-8">
        {/* Col 1: Flag Type dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={handleToggleDropdown}
            className={`flex items-center gap-4 rounded-full px-12 py-4 text-sm leading-none font-[570] ${selectedStyle}`}
          >
            {selectedLabel}
            <PalmettoIcon name="chevronDown" size="xs" />
          </button>

          {dropdownOpen && (
            <div className="mt-1 absolute top-full left-0 z-50 min-w-[180px] overflow-hidden rounded-12 border border-border-default bg-canvas-default shadow-md">
              <div className="relative border-b border-border-subtle px-8 py-4">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search..."
                  className="w-full bg-transparent pr-20 text-sm text-text-primary outline-none"
                />
                <button
                  type="button"
                  disabled={!searchValue}
                  onMouseDown={handleClearSearchMouseDown}
                  onClick={handleClearSearch}
                  className={`absolute top-1/2 right-8 -translate-y-1/2 cursor-pointer hover:text-text-primary ${!searchValue ? 'cursor-not-allowed opacity-30' : ''}`}
                  aria-label="Clear search"
                >
                  <PalmettoIcon name="x" size="xs" />
                </button>
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                {filteredTypes.map((type) => (
                  <DecisionTypeOption key={type} type={type} onSelect={handleSelectDecisionType} />
                ))}
                {filteredTypes.length === 0 && (
                  <p className="px-12 py-8 text-sm text-text-tertiary">No options found</p>
                )}
              </div>
            </div>
          )}
        </div>

        <PalmettoIcon name="chevronRight" size="xs" className="text-text-tertiary" />

        {/* Col 2: Label input (value mirrors label) */}
        <InlineEditableLabelInput
          variant="plain"
          defaultValue={watchedLabel}
          placeholder="Label"
          onCommit={handleLabelCommit}
        />

        {/* Delete */}
        <button
          type="button"
          onClick={handleRemove}
          className="cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary"
          aria-label="Remove option"
        >
          <PalmettoIcon name="trash" size="xs" />
        </button>
      </div>
      {isFlagCreatingDecisionType(watchedDecisionType) && (
        <OptionFlagMappingEditor
          inputIndex={inputIndex}
          optionIndex={optionIndex}
          control={control}
          setValue={setValue}
        />
      )}
    </div>
  );
};
